import React, { useEffect, useState, Suspense, useContext } from 'react';
import { BrowserRouter, Switch, Redirect } from 'react-router-dom';
import { AuthContext } from '../context/Auth/AuthContext';
import ColorModeContext from '../layout/themeContext';
import AppLoadingScreen from '../components/AppLoadingScreen';
import { dismissHtmlSplash } from '../utils/dismissHtmlSplash';
import LoggedInLayout from '../layout';
import ApiDocLayout from '../components/ApiDocLayout';
import NotFound from '../pages/NotFound';

const AppReadyGate = ({ children }) => {
  const { loading } = useContext(AuthContext);
  const { colorMode } = useContext(ColorModeContext);
  const themeReady = colorMode?.themeReady ?? true;
  if (loading || !themeReady) {
    return <AppLoadingScreen message="Entrando no sistema..." />;
  }
  return children;
};

/** Remove o splash HTML quando auth/tema estão prontos (rotas públicas como login). */
const SplashDismiss = () => {
  const { loading } = useContext(AuthContext);
  const { colorMode } = useContext(ColorModeContext);
  const themeReady = colorMode?.themeReady ?? true;

  useEffect(() => {
    if (!loading && themeReady) {
      dismissHtmlSplash();
    }
  }, [loading, themeReady]);

  return null;
};

const RouteLoadingFallback = () => <AppLoadingScreen message="Carregando página..." />;
const Cadastro = React.lazy(() => import('../pages/Cadastro'));
const Login = React.lazy(() => import('../pages/Login'));
const MobileLogin = React.lazy(() => import('../pages/MobileLogin'));
const Canais = React.lazy(() => import('../pages/Canais'));
const WhatsAppOfficial = React.lazy(() => import('../pages/WhatsAppOfficial'));
const ConnectionsCallback = React.lazy(() => import('../pages/ConnectionsCallback'));
const GoogleCalendarCallback = React.lazy(() => import('../pages/GoogleCalendarCallback'));
const Importacoes = React.lazy(() => import('../pages/Importacoes'));
const SettingsCustom = React.lazy(() => import('../pages/SettingsCustom'));
const SettingsEmpresas = React.lazy(() => import('../pages/SettingsEmpresas'));
const SettingsPlanos = React.lazy(() => import('../pages/SettingsPlanos'));
const SettingsWhitelabel = React.lazy(() => import('../pages/SettingsWhitelabel'));
const Contatos = React.lazy(() => import('../pages/Contatos'));
const ChatMoments = React.lazy(() => import('../pages/Moments'));
const Departamentos = React.lazy(() => import('../pages/Departamentos'));
const Etiquetas = React.lazy(() => import('../pages/Etiquetas'));
const DocumentacaoPage = React.lazy(() => import('../pages/Documentacao'));
const ApiClientesPage = React.lazy(() => import('../pages/api-clientes'));
const ApiMensagensPage = React.lazy(() => import('../pages/api-mensagens'));
const ApiContatosPage = React.lazy(() => import('../pages/api-contatos'));
const ApiTagsPage = React.lazy(() => import('../pages/api-tags'));
const ApiProdutosPage = React.lazy(() => import('../pages/api-produtos'));
const ApiServicosPage = React.lazy(() => import('../pages/api-servicos'));
const ApiUsuariosPage = React.lazy(() => import('../pages/api-usuarios'));
const ApiFilasPage = React.lazy(() => import('../pages/api-filas'));
const ApiTagsKanbanPage = React.lazy(() => import('../pages/api-tags-kanban'));
const ApiTarefasPage = React.lazy(() => import('../pages/api-tarefas'));
const ApiTicketsPage = React.lazy(() => import('../pages/api-tickets'));
const ApiConexoesPage = React.lazy(() => import('../pages/api-conexoes'));
const ApiFaturasPage = React.lazy(() => import('../pages/api-faturas'));
const GroupSchedulesPage = React.lazy(() => import('../pages/GroupSchedules'));
const GroupScheduleHistoryPage = React.lazy(() => import('../pages/GroupScheduleHistory'));
const PublicApiDocs = React.lazy(() => import('../pages/PublicApiDocs'));
const PublicDocs = React.lazy(() => import('../pages/PublicDocs'));
const Helps = React.lazy(() => import('../pages/Helps'));
const ContactLists = React.lazy(() => import('../pages/ContactLists'));
const ContactListItems = React.lazy(() => import('../pages/ContactListItems'));
const QuickMessages = React.lazy(() => import('../pages/QuickMessages'));
import { AuthProvider } from '../context/Auth/AuthContext';
import { PlanPermissionsProvider } from '../context/PlanPermissionsContext.jsx';
import { AiCreditsProvider } from '../context/AiCredits/AiCreditsContext.jsx';
import { TicketsContextProvider } from '../context/Tickets/TicketsContext.jsx';
import { WhatsAppsProvider } from '../context/WhatsApp/WhatsAppsContext.jsx';
import Route from './Route.jsx';
const Schedules = React.lazy(() => import('../pages/Lembretes'));
const Campanhas = React.lazy(() => import('../pages/Campanhas'));
const CampaignsConfig = React.lazy(() => import('../pages/CampaignsConfig'));
const FlowUp = React.lazy(() => import('../pages/Marketing/FlowUp'));
const MetaPixelPage = React.lazy(() => import('../pages/Marketing/MetaPixel'));
const NoResponseFollowUpPage = React.lazy(() => import('../pages/Marketing/NoResponseFollowUp'));
const CampaignReport = React.lazy(() => import('../pages/CampaignReport'));
const Annoucements = React.lazy(() => import('../pages/Annoucements'));
const AiCredits = React.lazy(() => import('../pages/AiCredits'));
const SystemMonitoring = React.lazy(() => import('../pages/SystemMonitoring'));
const PerformanceConfigPage = React.lazy(
  () => import('../pages/SystemMonitoring/PerformanceConfigPage')
);
const PerformanceDocsPage = React.lazy(
  () => import('../pages/SystemMonitoring/PerformanceDocsPage')
);
const Chat = React.lazy(() => import('../pages/Chat'));
const Agentes = React.lazy(() => import('../pages/Agentes'));
const ForgetPassword = React.lazy(() => import('../pages/ForgetPassWord/'));
const AllConnections = React.lazy(() => import('../pages/AllConnections'));
const Dashboard = React.lazy(() => import('../pages/Dashboard'));
const Home = React.lazy(() => import('../pages/Home'));
const FlowBuilderConfig = React.lazy(() =>
  import('../pages/FlowBuilderConfig').then((m) => ({ default: m.FlowBuilderConfig }))
);
const FlowBuilder = React.lazy(() => import('../pages/FlowBuilder'));
const CampaignsPhrase = React.lazy(() => import('../pages/CampaignsPhrase'));
const SubscriptionRoute = React.lazy(() => import('../pages/Subscription/SubscriptionRoute'));
const MinhaEmpresa = React.lazy(() => import('../pages/MinhaEmpresa'));
const FaturamentoEmpresa = React.lazy(() => import('../pages/FaturamentoEmpresa'));
const QueueIntegration = React.lazy(() => import('../pages/Integracao'));
const Produtos = React.lazy(() => import('../pages/Produtos'));
const Avaliacoes = React.lazy(() => import('../pages/Avaliacoes'));
const ProdutoForm = React.lazy(() => import('../pages/ProdutoForm'));
const ProdutosCategorias = React.lazy(() => import('../pages/ProdutosCategorias'));
const ProdutosAtributos = React.lazy(() => import('../pages/ProdutosAtributos'));
const CRM = React.lazy(() => import('../pages/CRM'));
const CrmPipelines = React.lazy(() => import('../pages/CrmPipelines'));
const AutomationTriggers = React.lazy(() => import('../pages/AutomationTriggers'));
const AutomationTriggerCanvas = React.lazy(() => import('../pages/AutomationTriggerCanvas'));
const CrmTrash = React.lazy(() => import('../pages/CrmTrash'));
const Tasks = React.lazy(() => import('../pages/Tasks'));
const empresa = React.lazy(() => import('../pages/Empresa'));
const IaWorkflows = React.lazy(() => import('../pages/IaWorkflows'));
const IaWorkflowEditor = React.lazy(() => import('../pages/IaWorkflowEditor'));
const TutorialVideos = React.lazy(() => import('../pages/TutorialVideos'));
const ServicosPage = React.lazy(() => import('../pages/Servicos'));
const ProfissionaisPage = React.lazy(() => import('../pages/Profissionais'));
const Sistema = React.lazy(() => import('../pages/Sistema'));
const PaymentCredentialsPage = React.lazy(() => import('../pages/PaymentCredentials'));
const FaturamentoPage = React.lazy(() => import('../pages/Faturamento'));
const FinanceiroSuperAdmin = React.lazy(() => import('../pages/FinanceiroSuperAdmin'));
const IntegrationSettings = React.lazy(() => import('../pages/IntegrationSettings'));
const FerramentasPage = React.lazy(() => import('../pages/Ferramentas'));
const ContactSettings = React.lazy(() => import('../pages/ContactSettings'));
const ContactAnalytics = React.lazy(() => import('../pages/ContactAnalytics'));
const Clients = React.lazy(() => import('../pages/Clientes'));
const ClienteDetalhe = React.lazy(() => import('../pages/ClienteDetalhe'));
const AgendaCalendars = React.lazy(() => import('../pages/AgendaCalendars'));
const PublicBooking = React.lazy(() => import('../pages/PublicBooking'));
const PublicBookingCancel = React.lazy(() => import('../pages/PublicBookingCancel'));
const Agenda = React.lazy(() => import('../pages/Agenda'));
const FormBuilder = React.lazy(() => import('../pages/FormBuilder'));
const FormBuilderConfig = React.lazy(() => import('../pages/FormBuilderConfig'));
const FormResults = React.lazy(() => import('../pages/FormResults'));
const PublicForm = React.lazy(() => import('../pages/PublicForm'));
const AppsPage = React.lazy(() => import('../pages/Apps'));
const SuperAdminAppsPage = React.lazy(() => import('../pages/SuperAdminApps'));
const SuperAdminNotifications = React.lazy(() => import('../pages/SuperAdminNotifications'));
const ContaBloqueada = React.lazy(() => import('../pages/ContaBloqueada'));
const PlanosDetalhes = React.lazy(() => import('../pages/Planos/Detalhes'));
const ProfilePage = React.lazy(() => import('../pages/Profile'));
const SystemSettingsHub = React.lazy(() => import('../pages/SystemSettings'));
const UsersPage = React.lazy(() => import('../pages/Users'));
const Roles = React.lazy(() => import('../pages/Roles'));
const RoleForm = React.lazy(() => import('../pages/RoleForm'));
const McpConnections = React.lazy(() => import('../pages/McpConnections'));
const UserFormPage = React.lazy(() => import('../pages/UserForm'));
const NoPermission = React.lazy(() => import('../pages/NoPermission'));

const FeatureDetailPage = React.lazy(() => import('../pages/landingpage/FeatureDetailPage'));
const TermsOfService = React.lazy(() => import('../pages/landingpage/TermsOfService'));
const UsagePolicy = React.lazy(() => import('../pages/landingpage/UsagePolicy'));
const PrivacyPolicy = React.lazy(() => import('../pages/landingpage/PrivacyPolicy'));
const PublicCheckout = React.lazy(() => import('../pages/PublicCheckout'));
const Atendimentos = React.lazy(() => import('../pages/Atendimentos'));
import { QueueSelectedProvider } from '../context/QueuesSelected/QueuesSelectedContext.jsx';
import NotificationToast from '../components/NotificationToast/index.jsx';
import { getSettingsPathWithTab } from '../pages/SystemSettings/constants';

const settingsTrashRedirect = () => <Redirect to={getSettingsPathWithTab('lixeira')} />;

/** Notificação de nova mensagem do contato: global quando logado (igual ideia do toast do sistema). */
const NotificationToastGlobal = () => {
  const { isAuth, loading } = useContext(AuthContext);
  if (loading || !isAuth) return null;
  return <NotificationToast />;
};

const Routes = () => {
  const [showCampaigns, setShowCampaigns] = useState(false);

  useEffect(() => {
    const cshow = localStorage.getItem('cshow');
    if (cshow !== undefined) {
      setShowCampaigns(true);
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <SplashDismiss />
        <PlanPermissionsProvider>
          <AiCreditsProvider>
            <NotificationToastGlobal />
            <TicketsContextProvider>
              <Suspense fallback={<RouteLoadingFallback />}>
                <Switch>
                  <Route exact path="/" component={Login} isPublic />
                  <Route exact path="/login" component={Login} isPublic />
                  <Route exact path="/solucoes/:slug" component={FeatureDetailPage} isPublic />
                  <Route exact path="/docs/public/api" component={PublicApiDocs} isPublic />
                  <Route path="/docs" component={PublicDocs} isPublic />
                  <Route exact path="/terms-of-service" component={TermsOfService} isPublic />
                  <Route exact path="/usage-policy" component={UsagePolicy} isPublic />
                  <Route exact path="/privacy-policy" component={PrivacyPolicy} isPublic />
                  <Route exact path="/mobile-login" component={MobileLogin} isPublic />
                  <Route exact path="/signup" component={Cadastro} isPublic />
                  <Route exact path="/forgetpsw" component={ForgetPassword} isPublic />
                  <Route exact path="/checkout/:token" component={PublicCheckout} isPublic />
                  <Route
                    exact
                    path="/book/:companySlug/:calendarSlug"
                    component={PublicBooking}
                    isPublic
                  />
                  <Route exact path="/booking/:token" component={PublicBookingCancel} isPublic />
                  <Route
                    exact
                    path="/smartform/:companySlug/:formSlug"
                    component={PublicForm}
                    isPublic
                  />
                  <Route exact path="/public/checkout/:token" component={PublicCheckout} isPublic />
                  <Route exact path="/integrations/meta" component={ConnectionsCallback} isPublic />
                  <Route
                    exact
                    path="/integrations/google-calendar"
                    component={GoogleCalendarCallback}
                    isPublic
                  />
                  <AppReadyGate>
                    <WhatsAppsProvider>
                      <QueueSelectedProvider>
                        <LoggedInLayout>
                          <Switch>
                            <Route exact path="/home" component={Home} isPrivate />
                            <Route
                              exact
                              path="/dashboard"
                              component={Dashboard}
                              isPrivate
                              permission="dashboard:read"
                            />
                            {/* A rota era /tickets/channels. Link antigo continua
                                em uso — notificação salva, favorito — e sem isto
                                cai no NotFound do catch-all. */}
                            <Redirect exact from="/tickets/channels" to="/connections" />
                            <Route
                              exact
                              path="/connections"
                              component={Canais}
                              isPrivate
                              permission="connections:read"
                            />
                            <Route
                              exact
                              path="/connections/whatsapp-official"
                              component={WhatsAppOfficial}
                              isPrivate
                              permission="whatsapp_templates:read"
                            />
                            <Route
                              exact
                              path="/tickets/imports"
                              component={Importacoes}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/tickets/quick-messages"
                              component={QuickMessages}
                              isPrivate
                              permission="quick_messages:read"
                            />
                            <Route
                              exact
                              path="/tickets/queues"
                              component={Departamentos}
                              isPrivate
                              permission="queues:read"
                            />
                            <Route
                              exact
                              path="/tickets/tags"
                              component={Etiquetas}
                              isPrivate
                              permission="tags:read"
                            />
                            <Route
                              exact
                              path="/tickets/schedules"
                              component={Schedules}
                              isPrivate
                              permission="schedules:read"
                            />
                            <Route
                              exact
                              path="/schedules"
                              isPrivate
                              render={() => <Redirect to="/tickets/schedules" />}
                            />
                            <Route
                              exact
                              path="/tickets/conversation/:ticketId?"
                              component={Atendimentos}
                              isPrivate
                              permission="tickets:read"
                            />
                            <Route
                              exact
                              path="/todolist"
                              component={Produtos}
                              isPrivate
                              permission="products:read"
                            />
                            <Route
                              exact
                              path="/sales/products"
                              component={Produtos}
                              isPrivate
                              permission="products:read"
                            />
                            <Route
                              exact
                              path="/avaliacoes"
                              component={Avaliacoes}
                              isPrivate
                              permission="dashboard:read"
                            />
                            <Route
                              exact
                              path="/sales/products/new"
                              component={ProdutoForm}
                              isPrivate
                              permission="products:create"
                            />
                            <Route
                              exact
                              path="/sales/categories"
                              component={ProdutosCategorias}
                              isPrivate
                              permission="products:manage_categories"
                            />
                            <Route
                              exact
                              path="/sales/attributes"
                              component={ProdutosAtributos}
                              isPrivate
                              permission="products:manage_attributes"
                            />
                            <Route
                              exact
                              path="/sales/products/:id"
                              component={ProdutoForm}
                              isPrivate
                              permission="products:update"
                            />
                            <Route
                              exact
                              path="/contacts/import-contacts"
                              component={Contatos}
                              isPrivate
                              permission="contacts:read"
                            />
                            <Route exact path="/helps" component={Helps} isPrivate />
                            <Route
                              exact
                              path="/users/new"
                              component={UserFormPage}
                              isPrivate
                              permission="users:create"
                            />
                            <Route
                              exact
                              path="/users/:userId/edit"
                              component={UserFormPage}
                              isPrivate
                              permission="users:update"
                            />
                            <Route exact path="/book/appointments" component={Agenda} isPrivate />
                            <Route
                              exact
                              path="/book/calendars"
                              component={AgendaCalendars}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/marketing/forms"
                              component={FormBuilder}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/marketing/group-schedules"
                              component={GroupSchedulesPage}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/marketing/group-schedules/:id/history"
                              component={GroupScheduleHistoryPage}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/marketing/forms/builder/:id?"
                              component={FormBuilderConfig}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/marketing/forms/:id/results"
                              component={FormResults}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/users"
                              component={UsersPage}
                              isPrivate
                              permission="users:read"
                            />
                            <Route
                              exact
                              path="/settings"
                              component={SystemSettingsHub}
                              isPrivate
                              permission="settings:read"
                            />
                            <Route
                              path="/admin/trash"
                              render={settingsTrashRedirect}
                              isPrivate
                              permission="settings:read"
                            />
                            <Route
                              exact
                              path="/system/users/new"
                              render={() => <Redirect to="/users/new" />}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/system/users/:userId/edit"
                              render={({ match }) => (
                                <Redirect to={`/users/${match.params.userId}/edit`} />
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/system/users/appointments"
                              render={() => <Redirect to="/book/appointments" />}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/system/users"
                              render={() => <Redirect to="/users" />}
                              isPrivate
                            />
                            <Route
                              path="/system/settings"
                              render={() => <Redirect to="/settings" />}
                              isPrivate
                              permission="settings:read"
                            />
                            <Route
                              path="/system/admin/trash"
                              render={settingsTrashRedirect}
                              isPrivate
                              permission="settings:read"
                            />
                            <Route
                              exact
                              path="/messages-api"
                              component={DocumentacaoPage}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/messages-api/documentacao"
                              render={() => (
                                <ApiDocLayout title="API de clientes">
                                  <ApiClientesPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-mensagens"
                              render={() => (
                                <ApiDocLayout title="API de mensagens">
                                  <ApiMensagensPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-contatos"
                              render={() => (
                                <ApiDocLayout title="API de contatos">
                                  <ApiContatosPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-tags"
                              render={() => (
                                <ApiDocLayout title="API de tags">
                                  <ApiTagsPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-produtos"
                              render={() => (
                                <ApiDocLayout title="API de produtos">
                                  <ApiProdutosPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-servicos"
                              render={() => (
                                <ApiDocLayout title="API de serviços">
                                  <ApiServicosPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-usuarios"
                              render={() => (
                                <ApiDocLayout title="API de usuários">
                                  <ApiUsuariosPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-filas"
                              render={() => (
                                <ApiDocLayout title="API de filas">
                                  <ApiFilasPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-tags-kanban"
                              render={() => (
                                <ApiDocLayout title="API de tags kanban">
                                  <ApiTagsKanbanPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-tarefas"
                              render={() => (
                                <ApiDocLayout title="API de tarefas">
                                  <ApiTarefasPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-tickets"
                              render={() => (
                                <ApiDocLayout title="API de tickets">
                                  <ApiTicketsPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-conexoes"
                              render={() => (
                                <ApiDocLayout title="API de conexões WhatsApp">
                                  <ApiConexoesPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/api-faturas"
                              render={() => (
                                <ApiDocLayout title="API de faturas">
                                  <ApiFaturasPage />
                                </ApiDocLayout>
                              )}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/settings"
                              component={SettingsCustom}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/whitelabel"
                              component={SettingsWhitelabel}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/companies"
                              component={SettingsEmpresas}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/plans"
                              component={SettingsPlanos}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/payment-credentials"
                              component={PaymentCredentialsPage}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/invoices"
                              component={FaturamentoPage}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/financial"
                              component={FinanceiroSuperAdmin}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/apps"
                              component={SuperAdminAppsPage}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/ai-credits"
                              component={AiCredits}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/notifications"
                              component={SuperAdminNotifications}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/monitoring"
                              component={SystemMonitoring}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/monitoring/performance"
                              component={PerformanceConfigPage}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/super-admin/monitoring/performance/docs"
                              component={PerformanceDocsPage}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/system/:categorySlug?"
                              component={Sistema}
                              isPrivate
                            />
                            <Route exact path="/tools" component={FerramentasPage} isPrivate />
                            <Route
                              exact
                              path="/queue-integration"
                              component={QueueIntegration}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/automations/integration"
                              component={QueueIntegration}
                              isPrivate
                              permission="integrations:read"
                            />
                            <Route
                              exact
                              path="/automations/mcp-connections"
                              component={McpConnections}
                              isPrivate
                              permission="integrations:mcp_read"
                            />
                            <Route exact path="/announcements" component={Annoucements} isPrivate />
                            <Route exact path="/company" component={empresa} isPrivate />
                            <Route
                              exact
                              path="/automations/integration/integration-settings"
                              component={IntegrationSettings}
                              isPrivate
                              permission="integrations:ecommerce_read"
                            />

                            <Route
                              exact
                              path="/marketing/campaign-by-phrase"
                              component={CampaignsPhrase}
                              isPrivate
                              permission="campaigns:read"
                            />
                            <Route
                              exact
                              path="/automations/flowbuilders"
                              component={FlowBuilder}
                              isPrivate
                              permission="flowbuilder:read"
                            />
                            <Route
                              exact
                              path="/automations/flowbuilder/:id?"
                              component={FlowBuilderConfig}
                              isPrivate
                              permission="flowbuilder:read"
                            />
                            <Route
                              exact
                              path="/chats/:id?"
                              component={Chat}
                              isPrivate
                              ownerOnly
                              requirePlanInternalChat
                            />
                            <Route exact path="/moments" component={ChatMoments} isPrivate />
                            <Route
                              exact
                              path="/crm"
                              component={CRM}
                              isPrivate
                              permission="crm:read"
                            />
                            <Route
                              exact
                              path="/crm/pipeline"
                              component={CRM}
                              isPrivate
                              permission="crm:read"
                            />
                            <Route
                              exact
                              path="/crm/funnels"
                              component={CrmPipelines}
                              isPrivate
                              permission="crm:manage_pipelines"
                            />
                            <Route
                              exact
                              path="/crm/pipelines"
                              isPrivate
                              render={() => <Redirect to="/crm/funnels" />}
                            />
                            {/* Gatilhos: saíram do CRM porque passaram a
                                atender evento de plataforma externa. As rotas
                                antigas redirecionam — link salvo e hábito de
                                quem já usa continuam valendo.
                                Ver docs/planos/plano-gatilhos-automacoes.md */}
                            <Route
                              exact
                              path="/automations/triggers"
                              component={AutomationTriggers}
                              isPrivate
                              permission="automations:manage_triggers"
                            />
                            <Route
                              exact
                              path="/automations/triggers/new"
                              component={AutomationTriggerCanvas}
                              isPrivate
                              permission="automations:manage_triggers"
                            />
                            <Route
                              exact
                              path="/automations/triggers/:id/edit"
                              component={AutomationTriggerCanvas}
                              isPrivate
                              permission="automations:manage_triggers"
                            />
                            {/* Link salvo para /canvas continua funcionando. */}
                            <Redirect
                              exact
                              from="/automations/triggers/:id/canvas"
                              to="/automations/triggers/:id/edit"
                            />
                            <Redirect exact from="/crm/automations" to="/automations/triggers" />
                            <Redirect
                              exact
                              from="/crm/automations/new"
                              to="/automations/triggers/new"
                            />
                            <Redirect
                              exact
                              from="/crm/automations/:id/edit"
                              to="/automations/triggers/:id/edit"
                            />
                            <Route
                              exact
                              path="/crm/trash"
                              component={CrmTrash}
                              isPrivate
                              permission="crm:delete"
                            />
                            <Route
                              exact
                              path="/tasks"
                              component={Tasks}
                              isPrivate
                              permission="tasks:read"
                            />
                            <Route
                              exact
                              path="/tasks/trash"
                              isPrivate
                              render={settingsTrashRedirect}
                            />
                            <Route
                              exact
                              path="/prompts"
                              component={Agentes}
                              isPrivate
                              permission="ia:read"
                            />
                            <Route
                              exact
                              path="/automations/agents"
                              component={Agentes}
                              isPrivate
                              permission="ia:read"
                            />
                            <Route
                              exact
                              path="/automations/ia-workflows"
                              component={IaWorkflows}
                              isPrivate
                              permission="ia:read"
                            />
                            <Route
                              exact
                              path="/automations/ia-workflows/:id"
                              component={IaWorkflowEditor}
                              isPrivate
                              permission="ia:read"
                            />
                            <Route
                              exact
                              path="/tutorial-videos"
                              component={TutorialVideos}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/sales/services"
                              component={ServicosPage}
                              isPrivate
                              permission="services:read"
                            />
                            <Route
                              exact
                              path="/profissionais"
                              component={ProfissionaisPage}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/sales/clients"
                              component={Clients}
                              isPrivate
                              permission="clients:read"
                            />
                            <Route
                              exact
                              path="/sales/clients/:id"
                              component={ClienteDetalhe}
                              isPrivate
                              permission="clients:read"
                            />
                            <Route
                              exact
                              path="/super-admin/allConnections"
                              component={AllConnections}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/apps"
                              component={AppsPage}
                              isPrivate
                              allowedProfiles={['admin', 'superadmin']}
                            />
                            <Route exact path="/roles" component={Roles} isPrivate ownerOnly />
                            <Route
                              exact
                              path="/roles/new"
                              component={RoleForm}
                              isPrivate
                              ownerOnly
                            />
                            <Route
                              exact
                              path="/roles/:id/edit"
                              component={RoleForm}
                              isPrivate
                              ownerOnly
                            />
                            <Route
                              exact
                              path="/settings/roles/new"
                              render={() => <Redirect to="/roles/new" />}
                              isPrivate
                              permission="settings:read"
                            />
                            <Route
                              exact
                              path="/settings/roles/:id/edit"
                              render={({ match }) => (
                                <Redirect to={`/roles/${match.params.id}/edit`} />
                              )}
                              isPrivate
                              permission="settings:read"
                            />
                            <Route
                              exact
                              path="/settings/roles"
                              render={() => <Redirect to="/roles" />}
                              isPrivate
                              permission="settings:read"
                            />
                            <Route
                              exact
                              path="/subscription/plans/details"
                              component={PlanosDetalhes}
                              isPrivate
                              ownerOnly
                            />
                            <Route
                              path="/subscription"
                              component={SubscriptionRoute}
                              isPrivate
                              ownerOnly
                            />
                            <Route
                              exact
                              path="/faturamento"
                              component={FaturamentoEmpresa}
                              isPrivate
                              ownerOnly
                            />
                            <Route
                              exact
                              path="/my-company"
                              component={MinhaEmpresa}
                              isPrivate
                              ownerOnly
                            />
                            <Route exact path="/profile" component={ProfilePage} isPrivate />
                            <Route
                              exact
                              path="/account-blocked"
                              component={ContaBloqueada}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/contacts/settings"
                              component={ContactSettings}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/contact-analytics"
                              component={ContactAnalytics}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/marketing/flowup"
                              component={FlowUp}
                              isPrivate
                              permission="followup:read"
                            />
                            <Route
                              exact
                              path="/marketing/no-response"
                              component={NoResponseFollowUpPage}
                              isPrivate
                              permission="no_response:read"
                            />
                            <Route
                              exact
                              path="/marketing/pixel-meta"
                              component={MetaPixelPage}
                              isPrivate
                              permission="marketing:manage_pixels"
                            />
                            <Route
                              exact
                              path="/marketing/mass-shooting"
                              component={Campanhas}
                              isPrivate
                              permission="mass_dispatch:read"
                            />
                            <Route
                              exact
                              path="/contacts/contact-lists"
                              component={ContactLists}
                              isPrivate
                              permission="contacts:group"
                            />
                            <Route
                              exact
                              path="/contacts/contact-lists/:contactListId/contacts"
                              component={ContactListItems}
                              isPrivate
                              permission="contacts:group"
                            />
                            {showCampaigns && (
                              <Route
                                exact
                                path="/marketing/mass-shooting/:campaignId/report"
                                component={CampaignReport}
                                isPrivate
                                permission="mass_dispatch:read"
                              />
                            )}
                            {showCampaigns && (
                              <Route
                                exact
                                path="/marketing/campaigns-config"
                                component={CampaignsConfig}
                                isPrivate
                                permission="campaigns:read"
                              />
                            )}
                            <Route exact path="/no-permission" component={NoPermission} isPrivate />
                            <Route component={NotFound} isPrivate />
                          </Switch>
                        </LoggedInLayout>
                      </QueueSelectedProvider>
                    </WhatsAppsProvider>
                  </AppReadyGate>
                </Switch>
              </Suspense>
            </TicketsContextProvider>
          </AiCreditsProvider>
        </PlanPermissionsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default Routes;
